/**
 * 拍照流程后台 · 落地态 slice
 *
 * 目的:进入应用(拉团队信息时)就并行判断"当前团队该看 Landing 还是 Dashboard",
 * 避免用户点开拍照流程页面才发请求造成的白屏 / 闪一下 landing 的观感。
 *
 * 判定规则(和团队信息接口 taskForm.formCount 的差异见 useTaskFlowRouteState):
 *   effective = forms.filter(f => !isDeleted || (taskStats.total ?? 0) > 0).length
 *   landing = effective === 0
 * 即:未删除的表算数;已删除的表只有还留下任务数据的才算数;
 *     软删空表(用户开过表 → 删了 → 无历史任务)不算,团队应回到 landing。
 *
 * 权限:userIDs / departmentIDs 走 createQueryPhotoAuth(和 /v5/workgroup/photos 一致),
 * 不同角色看到的表单集合有差异,landing 状态也随角色变化 —— 这是产品要的口径。
 *
 * 时间:不限(startTime=0, endTime=0),landing 判定和全局时间筛选无关。
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import type { RootState } from '../app/store'
import fetchHelper from '../utils/fetchHelper'
import { capturePromiseException } from '../utils/sentryUtils'
import { createQueryPhotoAuth } from '../app/authPolicyHooks'

declare const __API_SERVER_HOST__: string

/** /form/list 返回的单张表单结构,只声明 landing 判定用到的字段。 */
interface FormListItem {
  formID: number
  isDeleted: boolean
  taskStats?: {
    total: number
    completed: number
    pending: number
  }
}

interface TaskFlowLandingState {
  /** 当前落地态所属的团队 realID;切团队时用来判 stale 结果丢弃 */
  groupID: string
  /** true=Landing / false=Dashboard / null=未拉或拉失败(不下结论) */
  landing: boolean | null
  loading: boolean
  /** 失败原因;非 null 时 landing 通常为 null,组件按 landing===null 兜底 */
  error: string | null
}

const initialState: TaskFlowLandingState = {
  groupID: '',
  landing: null,
  loading: false,
  error: null
}

interface FetchTaskFlowLandingArgs {
  /** 团队 realID */
  groupID: string
}

/**
 * 打开团队 / 切团队时拉一次,把 landing 状态算好存进 slice。
 *
 * 权限:从 store 里读 group + userID,createQueryPhotoAuth 计算当前角色的可见范围,
 *      默认整团时 departmentIDList=[0](后端短路 GroupUsers)。
 * 失败:返 rejectWithValue,reducer 把 landing 置回 null,组件按 dashboard 兜底
 *      (宁可让"有流程"团队看到空 dashboard,也别把他们打回 landing 引导页)。
 */
export const fetchTaskFlowLandingState = createAsyncThunk<
  { groupID: string; landing: boolean },
  FetchTaskFlowLandingArgs,
  { state: RootState; rejectValue: string }
>(
  'taskFlowLanding/fetch',
  async ({ groupID }, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as any
      const userID: string = state.main.user?.user_id || ''
      const authPolicy = createQueryPhotoAuth(state.group, userID)

      // authPolicy 可能是 null(团队权限未就绪 / 无对应角色 policy)—— 兜整团
      const departmentIDs = (authPolicy?.departmentIDList ?? [0]).map(String)
      const userIDs = authPolicy?.userIDList ?? []

      const config = {
        url: __API_SERVER_HOST__ + '/web/workgroup/form/list',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            groupID,
            startTime: 0,
            endTime: 0,
            userIDs,
            departmentIDs
          })
        }
      }
      const result = await fetchHelper(dispatch, getState, config)
      // middleware.Response 外层是 { code, msg, data },真正 payload 在 data 里
      const payload = result?.data
      if (!payload || payload.status !== 0) {
        return rejectWithValue(
          `form/list status=${payload?.status} msg=${result?.msg ?? ''}`
        )
      }
      const forms: FormListItem[] = Array.isArray(payload.forms)
        ? payload.forms
        : []
      const effective = forms.filter(
        (f) => !f.isDeleted || (f.taskStats?.total ?? 0) > 0
      ).length
      return { groupID, landing: effective === 0 }
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue((error as Error)?.message ?? '网络错误')
    }
  }
)

const taskFlowLandingSlice = createSlice({
  name: 'taskFlowLanding',
  initialState,
  reducers: {
    /** 手动重置(比如退登 / 切团队前清理) */
    resetTaskFlowLanding: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTaskFlowLandingState.pending, (state, action) => {
        state.loading = true
        state.error = null
        state.groupID = action.meta.arg.groupID
        // pending 时保持上一次的 landing,避免切团队瞬间 landing 从 false 抖到 null
      })
      .addCase(
        fetchTaskFlowLandingState.fulfilled,
        (
          state,
          action: PayloadAction<{ groupID: string; landing: boolean }>
        ) => {
          // stale 结果:响应回来时用户已经切到别的团队,丢弃
          if (action.payload.groupID !== state.groupID) return
          state.loading = false
          state.landing = action.payload.landing
          state.error = null
        }
      )
      .addCase(fetchTaskFlowLandingState.rejected, (state, action) => {
        // stale 请求的失败也一并忽略
        if (action.meta.arg.groupID !== state.groupID) return
        state.loading = false
        state.landing = null
        state.error =
          (action.payload as string) ?? action.error.message ?? '未知错误'
      })
  }
})

export const { resetTaskFlowLanding } = taskFlowLandingSlice.actions
export default taskFlowLandingSlice.reducer
